import React, {
	useCallback,
	useEffect,
	useState,
	useRef,
} from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import get from "lodash/get";
import moment from "moment";
import _str from "shared/util/_str";
import Context from "shared/util/Context";
import FinishedBoostModal from "./FinishedBoostModal";
import ProgressBar from "shared/components/ProgressBar";
import BoostBoltIcon from "../icons/BoostBolt.svg";
import {
	selectedOptionOnNavBarEvent,
	selectedFirstPartyPromoEvent,
} from "../analytics";

import "./BoostButton.scss";

const MILLISECONDS = 1000;
const DEFAULT_BOOST_MULTIPLIER = 1.1;

function formatMilliseconds(milliseconds) {
	return moment(milliseconds).format("mm:ss");
}

const BoostButton = ({
	tokenCount,
	hasIncognitoEnabled,
	isBoosting,
	endTime,
	startTime,
	multiplier,
	isFinishedBoostModalOpen,
	closeFinishedBoostModal,
}) => {
	let text;

	if (isBoosting) {
		text = _str("BOOSTING");
	} else if (tokenCount) {
		text = _str(`Boost (${tokenCount})`);
	} else {
		text = _str("GET BOOST");
	}

	const [countdownTime, setCountdownTime] = useState();
	const [countdownProgress, setCountdownProgress] = useState();

	const startCountdownTimer = useRef();

	useEffect(() => {
		let timeLeftInMs = (endTime * MILLISECONDS) - new Date().getTime();

		if (!endTime || timeLeftInMs <= 1000) {
			return clearInterval(startCountdownTimer.current);
		}

		startCountdownTimer.current = setInterval(() => {
			timeLeftInMs -= 1000;
			const formattedTimeLeft = formatMilliseconds(timeLeftInMs);
			const boostTotalTimeInMS = (endTime - startTime) * MILLISECONDS;

			const progressBarTimeLeft = ((boostTotalTimeInMS - timeLeftInMs) / boostTotalTimeInMS) * 100;
			const progressBarCountdownTimeLeft = (100 - progressBarTimeLeft.toFixed(2));

			setCountdownTime(formattedTimeLeft);
			setCountdownProgress(`${progressBarCountdownTimeLeft}%`);

			if (!endTime || timeLeftInMs <= 1000) {
				clearInterval(startCountdownTimer.current);
			}
		}, 1000);

		return () => clearInterval(startCountdownTimer.current);
	}, [endTime, startTime]);


	const onClick = useCallback(() => {
		const boostModelLastPromoId = get(window.BoostController, "Model.data.lastpromoid");

		if (boostModelLastPromoId) {
			window.BoostController.closeReport();
		}

		window.BoostController.open("desktopnavbar");

		if (!tokenCount) {
			selectedOptionOnNavBarEvent("Boost Modal");
		}

		selectedFirstPartyPromoEvent("boost");
	}, [tokenCount]);
	const isStaff = Context.get("isStaff");

	const boostingText = countdownTime
		? `${multiplier || DEFAULT_BOOST_MULTIPLIER}x (${countdownTime})`
		: `${multiplier || DEFAULT_BOOST_MULTIPLIER}x`;

	const buttonDisplayText = isBoosting ? boostingText : text;

	return (
		<div className="navbar-boost">
			<button
				onClick={onClick}
				className={classnames("navbar-boost-button", {
					disabled: hasIncognitoEnabled,
				})}
				disabled={hasIncognitoEnabled}
			>
				<div className="navbar-boost-button-inner-container">
					<BoostBoltIcon />
					<span className={classnames("navbar-boost-text", {
						"navbar-boost-text--boosting": isBoosting,
					})}>
						{buttonDisplayText}
					</span>
				</div>
			</button>
			{
				isBoosting && !!isStaff &&
				<button
					className="navbar-boost-cancel-button"
					onClick={() => window.BoostController.cancel()}
				/>
			}
			{isBoosting &&
				<ProgressBar
					outerContainerClassName="navbar-boost-progress-container"
					progressPercentage={countdownProgress || "100%"}
					progressClassName="navbar-boost-progress-bar"
				/>
			}
			<FinishedBoostModal
				show={isFinishedBoostModalOpen}
				onClose={closeFinishedBoostModal}
				viewMultiplier={multiplier}
			/>
		</div>
	);
};

BoostButton.defaultProps = {
	tokenCount: 0,
	hasIncognitoEnabled: false,
	isBoosting: false,
	endTime: null,
	startTime: null,
	multiplier: 1.1,
	isFinishedBoostModalOpen: false,
};

BoostButton.propTypes = {
	tokenCount: PropTypes.number,
	hasIncognitoEnabled: PropTypes.bool,
	isBoosting: PropTypes.bool,
	endTime: PropTypes.number,
	startTime: PropTypes.number,
	multiplier: PropTypes.number,
	isFinishedBoostModalOpen: PropTypes.bool,
	closeFinishedBoostModal: PropTypes.func.isRequired,
};

export default BoostButton;



// WEBPACK FOOTER //
// ./src/shared/components/NavBar/BoostButton/BoostButton.jsx